import React from 'react';

/**
 * RubricaSection – renders a table of rubrica items (proventos, descontos, or informativos).
 *
 * Props:
 *   titulo       – section heading (e.g. "Proventos")
 *   itens        – array of { rubrica_nome, referencia, valor }
 *   cor          – tailwind text-color class applied to heading and value column
 *   informativo  – if true hides the "Ref." column
 */

const formatRef = (v) => {
  const n = Number(v ?? 0);
  return n > 0
    ? n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    : '';
};

const formatCurrency = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function RubricaSection({ titulo, itens, cor, informativo }) {
  if (!itens?.length) return null;

  return (
    <div>
      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${cor}`}>
        {titulo}
      </h4>

      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[color:var(--border)]">
            <th className="text-left py-1.5 text-[color:var(--text-muted)] font-medium">
              Descrição
            </th>
            {!informativo && (
              <th className="text-right py-1.5 text-[color:var(--text-muted)] font-medium w-20">
                Ref.
              </th>
            )}
            <th className="text-right py-1.5 text-[color:var(--text-muted)] font-medium w-28">
              Valor
            </th>
          </tr>
        </thead>
        <tbody>
          {itens.map((item, idx) => (
            <tr
              key={idx}
              className="border-b border-[color:var(--border)]/50 last:border-0"
            >
              <td className="py-1.5 text-[color:var(--text)]">
                {item.rubrica_nome}
              </td>
              {!informativo && (
                <td className="py-1.5 text-right text-[color:var(--text-muted)]">
                  {formatRef(item.referencia)}
                </td>
              )}
              <td className={`py-1.5 text-right font-medium ${cor}`}>
                {formatCurrency(item.valor)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Helper sub-components also used by FolhaTab ── */

/**
 * StatusBadge map for folha statuses.
 */
export const FOLHA_STATUS_MAP = {
  aberta: {
    label: 'Aberta',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  calculando: {
    label: 'Calculando',
    className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  calculada: {
    label: 'Calculada',
    className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  },
  fechada: {
    label: 'Fechada',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  cancelada: {
    label: 'Cancelada',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
};

/**
 * Tipo labels for folha types.
 */
export const FOLHA_TIPO_LABELS = {
  mensal: 'Mensal',
  '13_primeiro': '13º - 1ª Parcela',
  '13_segundo': '13º - 2ª Parcela',
  ferias: 'Férias',
  rescisao: 'Rescisão',
  complementar: 'Complementar',
};

/**
 * Currency formatter helper.
 */
export const fmtBRL = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Small stat display used inside folha cards.
 */
export function FolhaStat({ label, value, cor, bold }) {
  return (
    <div className="text-sm">
      <span className="text-[color:var(--text-muted)]">{label}: </span>
      <span className={`${bold ? 'font-bold' : 'font-medium'} ${cor}`}>{value}</span>
    </div>
  );
}

/**
 * Info field used in holerite header.
 */
export function InfoField({ label, valor, span }) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <span className="text-[color:var(--text-muted)]">{label}: </span>
      <span className="font-medium text-[color:var(--text)]">{valor}</span>
    </div>
  );
}
